import { Core } from "@faire/design-tokens";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import React, { type JSX } from "react";
import styled from "styled-components";

export interface PillProps {
  pillContent: string | undefined;
  pillIcon?: JSX.Element;
  linkTo: string | undefined;
  onClick?: () => void;
  pillImage?: IImage;
}

const SQUARE_DIMENSION = 32;
export const Pill: React.FC<{ pill: PillProps; className?: string }> = ({
  pill,
  className,
}) => {
  const { pillContent, linkTo = "", onClick, pillIcon, pillImage } = pill;
  const isSSR = useIsSSR();
  const imageUrl = pillImage
    ? optimizeImageUrlFillSquare(pillImage, isSSR, SQUARE_DIMENSION, {
        ignoreResizeTag: true,
      })
    : undefined;
  if (!pillContent) {
    return null;
  }
  if (imageUrl) {
    return <PillWithImage pill={pill} imageUrl={imageUrl} />;
  }
  return (
    <PillWrapper to={linkTo} onClick={onClick} className={className}>
      {pillIcon ? (
        <>
          {pillIcon}
          <Spacer width="1x" />
        </>
      ) : null}
      <PillContent>{pillContent}</PillContent>
    </PillWrapper>
  );
};

const PillWithImage: React.FC<{ pill: PillProps; imageUrl: string }> = ({
  pill,
  imageUrl,
}) => {
  const { pillContent, linkTo = "", onClick } = pill;

  return (
    <PillWithImageWrapper to={linkTo} onClick={onClick}>
      <img src={imageUrl} alt={pillContent} />
      <Spacer width="1x" />
      <PillContent>{pillContent}</PillContent>
    </PillWithImageWrapper>
  );
};

const PillWrapper = styled(Link)`
  ${TypographyStyles({})}
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;

  height: ${getSpacing("5x")};
  border-radius: ${getSpacing("5x")};
  cursor: pointer;
  border: 1px solid ${Core.border.muted};

  padding: ${getSpacing(10, "2x")};

  ${responsiveMediaQueries.canHover} {
    &:hover {
      border-color: ${Core.border.default};
    }
    &:focus-visible {
      outline: 2px solid -webkit-focus-ring-color;
      outline-offset: -2px;
    }
  }
`;

const PillWithImageWrapper = styled(PillWrapper)`
  padding-left: 4px;
  border-radius: 50px;
  background-color: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  img {
    display: block;
    border-radius: 50%;
    width: ${SQUARE_DIMENSION}px;
    height: ${SQUARE_DIMENSION}px;
  }

  ${responsiveMediaQueries.canHover} {
    &:hover {
      border-color: ${Core.border.default};
    }
    &:focus-visible {
      outline: 2px solid -webkit-focus-ring-color;
      outline-offset: -2px;
    }
  }
`;

const PillContent = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
`;
